//
// Copyright (c) 2021 Astute Global, Inc.
//

import { Command } from "ckeditor5";
import { areaIdsToAreaNames, getPrivilegedAreaElement } from "./util";

export default class PrivilegedAreaCommand extends Command {
  constructor(editor) {
    super(editor);
    this.set("areaIds", "");
    this.set("areaNames", "");
  }

  execute(options = {}) {
    const model = this.editor.model;
    const document = model.document;
    const areaIds = options.areaIds || this.areaIds;
    model.change((writer) => {
      const privilegedAreaElement = getPrivilegedAreaElement(
        options.selection || document.selection,
      );
      const selection = options.selection || document.selection;
      if (!areaIds || areaIds.length === 0) {
        if (privilegedAreaElement) {
          this._removePrivilegedAreasFromElement(writer, privilegedAreaElement);
        } else {
          this._removePrivilegedAreasFromBlocks(writer, selection);
        }
      } else {
        const areaNames =
          options.areaNames || this.areaNames || areaIdsToAreaNames(this.editor, areaIds);
        if (privilegedAreaElement) {
          this._updatePrivilegedAreas(writer, privilegedAreaElement, areaIds, areaNames);
        } else {
          this._addPrivilegedAreas(writer, selection, areaIds, areaNames, model);
        }
      }
    });
  }

  refresh() {
    const model = this.editor.model;
    const selection = model.document.selection;
    const privilegedAreaElement = getPrivilegedAreaElement(selection);

    if (privilegedAreaElement) {
      this.value = privilegedAreaElement;
      this.areaIds = privilegedAreaElement.getAttribute("areaIds");
      this.isEnabled = true;
    } else {
      this.value = null;
      this.areaIds = "";
      this.isEnabled = !!model.schema.findAllowedParent(
        selection.getFirstPosition(),
        "privilegedArea",
      );
    }
  }

  _addPrivilegedAreas(writer, selection, areaIds, areaNames, model) {
    const privilegedArea = writer.createElement("privilegedArea", {
      areaIds: areaIds,
      areaNames: areaNames,
    });
    if (!selection.isCollapsed) {
      // NOTE: writer.wrap() looks like it would be great for this.  But, if the selection range
      // only has the root element as its parent, wrap eventually fails with
      // view-writer-invalid-range-container
      const selectedContent = model.getSelectedContent(selection);
      let onlyText = true;
      for (const node of selectedContent.getChildren()) {
        if (!node.is("$text") && !node.is("$textProxy")) {
          onlyText = false;
        }
      }
      // Must wait until after getting the selection content, otherwise it will be lost
      model.insertObject(privilegedArea, selection, null, { setSelection: "after" });
      if (onlyText) {
        const paragraph = writer.createElement("paragraph");
        writer.append(selectedContent, paragraph);
        writer.append(paragraph, privilegedArea);
      } else {
        writer.append(selectedContent, privilegedArea);
      }
    } else {
      model.insertObject(privilegedArea, selection, null, { setSelection: "after" });
      // To work around a CKEditor 5 bug, must append a paragraph to the Accordion Section Body.
      // If it is not included, the body will not be able to be edited by the user.
      const privilegedAreaParagraph = writer.createElement("paragraph");
      writer.append(privilegedAreaParagraph, privilegedArea);
      writer.appendText(
        this.editor.config.get("privilegedArea.placeholderName"),
        privilegedAreaParagraph,
      );
    }
  }

  _removePrivilegedAreasFromBlocks(writer, selection) {
    const blocks = Array.from(selection.getSelectedBlocks());
    for (const block of blocks) {
      if (block.is("element", "privilegedArea")) {
        writer.unwrap(block);
      }
    }
  }

  _removePrivilegedAreasFromElement(writer, element) {
    writer.unwrap(element);
  }

  _updatePrivilegedAreas(writer, privilegedAreaElement, areaIds, areaNames) {
    writer.setAttribute("areaIds", areaIds, privilegedAreaElement);
    writer.setAttribute("areaNames", areaNames, privilegedAreaElement);
  }
}
